import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import FormContainer from 'components/form/FormContainer';
import FormControl from 'components/form/FormControl';
import FormField from './FormField';
import Button from 'components/Button';
import { getMediaUrl, uploadMedia } from 'services/media';
import { useWatch } from 'react-hook-form';
import { isUrl, isValidEmail } from 'utils';

const ValidationRules = {
  email: {
    fn: isValidEmail,
    message: 'Please enter a valid email address',
  },
  url: {
    fn: isUrl,
    message: 'Please enter a valid URL including http:// or https://',
  },
  name: {
    fn: (val) => {
      const { first_name, last_name } = val;
      return !!first_name && !!last_name;
    },
  },
};

function shouldRender(field, groups) {
  if (field.visibility?.groups?.length) {
    if (!groups || !groups.length) return false;
    return field.visibility.groups.some((g) => groups.includes(g));
  }
  return true;
}

const ConditionalField = ({ field }) => {
  const groups = useWatch({
    name: 'group_ids',
    exact: true,
    disabled: !field.visibility?.groups?.length,
  });

  const validator = (val) => {
    const rule = ValidationRules[field.fieldType];
    if (rule && val) {
      const isValid = rule.fn(val);
      if (!isValid) {
        return 'Please check your input';
      }
    }
    return true;
  };

  if (shouldRender(field, groups)) {
    return (
      <FormControl
        name={field.fieldName}
        rules={{
          validate: validator,
        }}
        required={field.isRequired}
      >
        <FormField margin="normal" field={field} />
      </FormControl>
    );
  }

  return null;
};

const Form = ({ formData, groups, handleSubmit, initialValues }) => {
  const groupOptions = groups
    ? groups.map((g) => ({
        label: g.group_name,
        value: g.group_id,
      }))
    : [];

  const fields = useMemo(() => {
    const { form_fields_json } = formData;
    if (typeof form_fields_json === 'string') {
      return JSON.parse(form_fields_json).fields;
    }
    return form_fields_json.fields;
  }, []);

  const onSubmit = async (values) => {
    const result = {};
    const keys = Object.keys(values);

    for (const key of keys) {
      const field = fields.find((f) => f.fieldName === key);
      if (!field && key === 'group_ids') {
        result[key] = values[key];
        continue;
      }
      switch (field.fieldType) {
        case 'date': {
          result[key] = values[key]?.format('YYYY-MM-DD');
          break;
        }
        case 'csv':
        case 'logo':
        case 'file': {
          const file = values[key]?.[0];
          if (file) {
            if (file instanceof File) {
              const { location } = await uploadMedia(file);
              result[key] = await getMediaUrl(location);
            } else if (file.preview) {
              result[key] = file.preview;
            }
          }
          break;
        }
        default: {
          result[key] = values[key];
        }
      }
    }
    await handleSubmit(result);
  };

  return (
    <Box>
      <Box mb={4}>
        <Typography variant="h1" gutterBottom>
          {formData.form_name}
        </Typography>
        <Typography variant="h4">{fields.form_description}</Typography>
      </Box>
      <Paper variant="outlined" sx={{ p: 3, mx: 'auto', maxWidth: 700 }}>
        <FormContainer onSuccess={onSubmit} defaultValues={initialValues}>
          {({ submitForm }) => (
            <div>
              {groupOptions && (
                <FormControl name="group_ids" required="Please select groups">
                  <FormField
                    field={{
                      options: groupOptions,
                      fieldType: 'checkbox',
                      fieldLabel: 'Groups',
                      fieldHint:
                        'Please select the groups you would like us to create for you.',
                    }}
                  />
                </FormControl>
              )}
              {fields.map((field) => {
                const isFormElement = ![
                  'header',
                  'image',
                  'button',
                  'divider',
                ].includes(field.fieldType);

                if (!isFormElement) {
                  return (
                    <FormField
                      sx={{ mt: 2, mb: 1 }}
                      key={field.id}
                      margin="normal"
                      field={field}
                    />
                  );
                }

                return <ConditionalField key={field.id} field={field} />;
              })}
              <Divider sx={{ my: 3 }} />
              <Box mt={2} textAlign="right">
                <Button size="large" onClick={submitForm}>
                  Submit
                </Button>
              </Box>
            </div>
          )}
        </FormContainer>
      </Paper>
    </Box>
  );
};

export default Form;
